import React, { useEffect, useState } from "react";
import axiosInstance from "../axios/axios";
import uploadBtn from "../assets/uploadBtn.svg";
import { Link } from "react-router-dom";
import ErrorMessage from "./ErrorMessage";
import Loader from "./Loader";

const CompanyApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [viewedApplication, setViewedApplication] = useState([]);
  const org_id = localStorage.getItem("role_specific_id");

  useEffect(() => {
    const fetchCompanyApplication = async () => {
      setLoading(true);

      if (!org_id) {
        setError("Error fetching interviews:");
        console.error("Organization ID is missing.");
        return;
      }

      try {
        const res = await axiosInstance(`ViewApplicationByOrg/${org_id}`);
        console.log("Applications fetched:", res.data.applications);
        setApplications(res.data.applications || []);
      } catch (error) {
        setError("Error fetching Applications:");
        console.error("Error fetching applications:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyApplication();
  }, [org_id]);

  // Utility function to calculate "days ago"
  const calculateDaysAgo = (createdAt) => {
    const createdDate = new Date(createdAt);
    const today = new Date();

    // Normalize to midnight for accurate date comparisons
    today.setHours(0, 0, 0, 0);
    createdDate.setHours(0, 0, 0, 0);

    const timeDifference = createdDate - today; // Difference in milliseconds
    const daysAgo = Math.floor(timeDifference / (1000 * 60 * 60 * 24)); // Convert to days

    if (daysAgo === 0) return "Today";
    if (daysAgo === -1) return "Yesterday";
    if (daysAgo < 0) return `${Math.abs(daysAgo)} days ago`; // Past dates
    return `In ${daysAgo} days`; // Future dates
  };

  // Truncate text helper
  const truncateText = (text, maxLength) => {
    if (!text) return "No cover letter provided.";
    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return "UN";
    return name
      .split(" ")
      .map(word => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Get status badge configuration
  const getStatusConfig = (status) => {
    const configs = {
      Applied: {
        text: "Applied",
        className: "bg-blue-50 text-blue-700 border-blue-200"
      },
      Accepted: {
        text: "Accepted",
        className: "bg-green-50 text-green-700 border-green-200"
      },
      Viewed: {
        text: "Viewed",
        className: "bg-yellow-50 text-yellow-700 border-yellow-200"
      },
      Interview: {
        text: "Interview",
        className: "bg-purple-50 text-purple-700 border-purple-200"
      },
      Rejected: {
        text: "Rejected",
        className: "bg-red-50 text-red-700 border-red-200"
      }
    };
    return configs[status] || configs.Applied;
  };

  const handleViewApplication = async (id) => {
    try {
      const res = await axiosInstance.put(`ReviewApplicationByOrg/${id}`, {
        org_id, // Ensure this is sent as a JSON object
      });
      setViewedApplication(res.data.application);
      console.log("Viewed Application:", res.data.application);
    } catch (error) {
      console.error("Error viewing application:", error);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 sm:px-6 py-4 flex justify-between items-center">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
            Applications
          </h1>
          <Link
            to="/dashboard/create-job"
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors duration-200 shadow-sm"
          >
            <img src={uploadBtn} alt="" className="w-4 h-4" />
            <span className="hidden sm:inline">Create Job</span>
            <span className="sm:hidden">Create</span>
          </Link>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-6">
        {/* Error State */}
        {error && (
          <div className="mb-6">
            <ErrorMessage message={error} />
          </div>
        )}

        {/* Applications Summary */}
        {applications.length > 0 && (
          <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-gray-900">
                  Total Applications
                </h2>
                <p className="text-sm text-gray-600">
                  Manage and review candidate applications
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-green-600">
                  {applications.length}
                </div>
                <div className="text-sm text-gray-500">Applications</div>
              </div>
            </div>
          </div>
        )}

        {/* Applications Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {applications.length > 0 ? (
            applications.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 flex flex-col"
              >
                {/* Applicant Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-green-700 font-semibold text-sm">
                        {getInitials(item?.user?.student?.full_name)}
                      </span>
                    </div>
                    
                    {/* Applicant Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {item?.user?.student?.full_name || "Unknown Applicant"}
                      </h3>
                      <p className="text-sm text-gray-600 truncate">
                        {item?.user?.student?.university || "Unknown University"}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Applied {calculateDaysAgo(item.created_at)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Status Badge */}
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusConfig(item.status).className} flex-shrink-0`}>
                    {getStatusConfig(item.status).text}
                  </span>
                </div>

                {/* Job Title */}
                <div className="mb-4">
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                    {item?.job?.job_title || "Unknown Job Title"}
                  </div>
                </div>

                {/* Cover Letter Preview */}
                <div className="mb-6 flex-1">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Cover Letter</h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {truncateText(item.cover_letter, 120)}
                  </p>
                </div>

                {/* Action Button */}
                <div className="pt-4 border-t border-gray-100">
                  <Link
                    to={`/dashboard/applications/${item.id}`}
                    className="w-full inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors duration-200 shadow-sm"
                    state={{ application: viewedApplication }}
                    onClick={() => handleViewApplication(item.id)}
                  >
                    View Application
                    <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            ))
          ) : (
            // Empty State
            <div className="col-span-full">
              <div className="text-center py-12">
                <div className="mb-4">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Applications Yet
                </h3>
                <p className="text-gray-500 mb-6">
                  You haven't received any job applications. Create a job posting to start receiving applications.
                </p>
                <Link
                  to="/dashboard/create-job"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors duration-200 shadow-sm"
                >
                  <img src={uploadBtn} alt="" className="w-4 h-4" />
                  Create Your First Job
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyApplications;